import { InventoryItem, Quest, QuestLog } from '../../../shared/CharacterTypes';
import { getAvailableQuests, getQuest, QUEST_COMPLETION_MESSAGES, QUEST_ITEM_DROPS } from '../../../shared/QuestData';
import { gameLogger } from '../../../utils/GameLogger';
import { npcTextPlain } from '../../../shared/content/npcTextMarkup';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { ITEM_DATABASE } from '../../../shared/ItemData';
import { EventBus, EVENTS } from '../../../shared/EventBus';

export class QuestSystem {
  private static instance: QuestSystem;
  private eventBus = EventBus.getInstance();
  private questLog: QuestLog = {
    active: [],
    completed: []
  };
  private questListeners: Map<string, Function[]> = new Map();

  private constructor() {
    this.initializeQuestLog();
  }

  public static getInstance(): QuestSystem {
    if (!QuestSystem.instance) {
      QuestSystem.instance = new QuestSystem();
    }
    return QuestSystem.instance;
  }

  private initializeQuestLog(): void {
    // Load quest progress from save or start fresh
    this.questLog = {
      active: [],
      completed: []
    };
    gameLogger.info('QuestSystem', 'Quest log initialized');
  }

  /**
   * Accept a quest from an NPC
   */
  public acceptQuest(questId: string): boolean {
    gameLogger.debug('quest', `Attempting to accept quest ${questId}`, { questId });

    const questDef = getQuest(questId);
    if (!questDef) {
      gameLogger.error('quest', `Quest ${questId} not found`);
      return false;
    }

    // Check if already completed
    if (this.questLog.completed.includes(questId)) {
      gameLogger.warn('quest', `Quest ${questId} already completed`, { questId });
      return false;
    }

    // Check if already active
    if (this.questLog.active.some((q: Quest) => q.id === questId)) {
      gameLogger.warn('quest', `Quest ${questId} already active`, { questId });
      return false;
    }

    // Create new quest instance
    const quest: Quest = {
      id: questDef.id,
      name: questDef.name,
      description: questDef.description,
      type: questDef.type,
      targetId: questDef.objectives[0]?.targetId || '',
      targetCount: questDef.objectives[0]?.targetCount || 0,
      currentCount: 0,
      completed: false,
      rewards: questDef.rewards
    };

    this.questLog.active.push(quest);
    gameLogger.info('QuestSystem', `Accepted quest: ${questId}`);

    // Emit quest accepted event
    this.emitQuestEvent('questAccepted', quest);
    this.eventBus.emit(EVENTS.QUEST_ACCEPTED, quest);

    return true;
  }

  /**
   * Update quest progress for kill objectives
   */
  public updateKillProgress(monsterId: string, count: number = 1): void {
    const killQuests = this.questLog.active.filter(q =>
      q.type === 'kill' && q.targetId === monsterId && !q.completed
    );

    killQuests.forEach(quest => {
      const oldCount = quest.currentCount;
      quest.currentCount = Math.min(quest.currentCount + count, quest.targetCount);

      if (quest.currentCount !== oldCount) {
        gameLogger.debug('QuestSystem', `Quest ${quest.id} progress: ${quest.currentCount}/${quest.targetCount}`);
        this.emitQuestEvent('questProgress', { quest, oldCount, newCount: quest.currentCount });
        this.eventBus.emit(EVENTS.QUEST_PROGRESS, { quest, oldCount, newCount: quest.currentCount });
      }

      // Objective target reached: mark ready, but keep the quest active until
      // an explicit NPC hand-in calls completeQuest().
      if (quest.currentCount >= quest.targetCount) {
        quest.completed = true;
      }
    });
  }

  /**
   * Update quest progress for collection objectives
   */
  public updateCollectionProgress(itemId: string, count: number = 1): void {
    const collectionQuests = this.questLog.active.filter(q =>
      q.type === 'collect' && q.targetId === itemId && !q.completed
    );

    collectionQuests.forEach(quest => {
      const oldCount = quest.currentCount;
      quest.currentCount = Math.min(quest.currentCount + count, quest.targetCount);

      if (quest.currentCount !== oldCount) {
        gameLogger.debug('QuestSystem', `Quest ${quest.id} progress: ${quest.currentCount}/${quest.targetCount}`);
        this.emitQuestEvent('questProgress', { quest, oldCount, newCount: quest.currentCount });
        this.eventBus.emit(EVENTS.QUEST_PROGRESS, { quest, oldCount, newCount: quest.currentCount });
      }

      // Ready for NPC hand-in; collection progress alone never grants rewards.
      if (quest.currentCount >= quest.targetCount) {
        quest.completed = true;
      }
    });
  }

  /**
   * Complete a quest and give rewards
   */
  public completeQuest(questId: string): boolean {
    gameLogger.debug('quest', `Attempting to complete quest ${questId}`, { questId });

    const questIndex = this.questLog.active.findIndex((q: Quest) => q.id === questId);
    if (questIndex === -1) {
      gameLogger.error('quest', `Quest ${questId} not found in active quests`);
      return false;
    }

    const quest = this.questLog.active[questIndex];
    if (!quest.completed) {
      gameLogger.warn('quest', `Quest ${questId} objectives are not ready for hand-in`, { questId });
      return false;
    }

    // Move to completed
    this.questLog.active.splice(questIndex, 1);
    this.questLog.completed.push(questId);

    gameLogger.info('quest', `Completed quest: ${questId}`, {
      questId,
      questName: quest.name,
      rewards: quest.rewards
    });

    // Show completion message
    // Plain text: this event's consumers show the line without NPC text markup.
    const completionMessage = npcTextPlain(QUEST_COMPLETION_MESSAGES[questId] || 'Quest completed!',
      { itemName: id => ITEM_DATABASE[id]?.name, abilityName: id => getAbilityData(id)?.name });

    // Emit quest completed event
    this.emitQuestEvent('questCompleted', { quest, rewards: quest.rewards, message: completionMessage });
    this.eventBus.emit(EVENTS.QUEST_COMPLETED, { quest, rewards: quest.rewards, message: completionMessage });

    // Grant experience via event (ProgressionSystem listens to this)
    if (quest.rewards && quest.rewards.xp) {
      this.eventBus.emit(EVENTS.GAIN_XP, { amount: quest.rewards.xp });
    }

    return true;
  }

  /**
   * Get available quests for a player level
   */
  public getAvailableQuests(playerLevel: number, completedQuests: string[]): Quest[] {
    const available = getAvailableQuests(playerLevel, completedQuests);

    return available.map(questDef => ({
      id: questDef.id,
      name: questDef.name,
      description: questDef.description,
      type: questDef.type,
      targetId: questDef.objectives[0]?.targetId || '',
      targetCount: questDef.objectives[0]?.targetCount || 0,
      currentCount: 0,
      completed: false,
      rewards: questDef.rewards
    }));
  }

  /**
   * Get quest by ID
   */
  public getQuest(questId: string): Quest | undefined {
    return this.questLog.active.find(q => q.id === questId);
  }

  /**
   * Get all active quests
   */
  public getActiveQuests(): Quest[] {
    return [...this.questLog.active];
  }

  /**
   * Get completed quest IDs
   */
  public getCompletedQuests(): string[] {
    return [...this.questLog.completed];
  }

  /**
   * Check if a quest is completed
   */
  public isQuestCompleted(questId: string): boolean {
    return this.questLog.completed.includes(questId);
  }

  /**
   * Get quests offered by a specific NPC
   */
  public getNpcQuests(npcId: string, playerLevel: number): Quest[] {
    const available = this.getAvailableQuests(playerLevel, this.questLog.completed);
    return available.filter(quest => {
      const questDef = getQuest(quest.id);
      return questDef?.startNpc === npcId;
    });
  }

  /**
   * Check if NPC has quests for player
   */
  public npcHasQuests(npcId: string, playerLevel: number): boolean {
    return this.getNpcQuests(npcId, playerLevel).length > 0;
  }

  /**
   * Handle monster drops for quest items
   */
  public handleMonsterDrop(monsterId: string): InventoryItem | null {
    const dropData = QUEST_ITEM_DROPS[monsterId];
    if (!dropData) return null;

    // Check if player has quest that needs this item
    const hasRelevantQuest = dropData.requiredForQuests.some(questId =>
      this.questLog.active.some(q => q.id === questId && q.type === 'collect')
    );

    if (!hasRelevantQuest) return null;

    if (Math.random() < dropData.dropRate) {
      return {
        id: dropData.itemId,
        name: dropData.itemName,
        type: 'material',
        quantity: 1,
        maxStack: 99,
        description: `A ${dropData.itemName.toLowerCase()} dropped by ${monsterId}`
      };
    }

    return null;
  }

  /**
   * Load quest progress from save data
   */
  public loadQuestProgress(questLog: QuestLog): void {
    this.questLog = {
      active: [...questLog.active],
      completed: [...questLog.completed]
    };
    gameLogger.info('QuestSystem', `Loaded quest progress: ${this.questLog.active.length} active, ${this.questLog.completed.length} completed`);
  }

  /**
   * Save quest progress for persistence
   */
  public saveQuestProgress(): QuestLog {
    return {
      active: this.questLog.active.map(q => ({ ...q })),
      completed: [...this.questLog.completed]
    };
  }

  /**
   * Listen for quest events
   */
  public on(event: string, callback: Function): void {
    if (!this.questListeners.has(event)) {
      this.questListeners.set(event, []);
    }
    this.questListeners.get(event)!.push(callback);
  }

  /**
   * Emit quest events to legacy listeners (backward compatibility)
   */
  private emitQuestEvent(event: string, data: any): void {
    const listeners = this.questListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  /**
   * Debug: Log all quest states
   */
  public debugLogState(): void {
    gameLogger.debug('quest', '=== QUEST SYSTEM STATE ===');
    gameLogger.debug('quest', `Active Quests: ${this.questLog.active.length}`);
    this.questLog.active.forEach(q => {
      gameLogger.debug('quest', `  - ${q.name}: ${q.currentCount}/${q.targetCount} (${q.completed ? '✓' : '○'})`);
    });
    gameLogger.debug('quest', `Completed Quests: ${this.questLog.completed.length}`);
    gameLogger.debug('quest', '===========================');
  }
}

// Singleton instance
export const questSystem = QuestSystem.getInstance();
